import React, { useMemo } from 'react';
import { Calendar, CheckCircle2, ClipboardCheck } from 'lucide-react';
import type { Indicator, IndicatorSnapshot } from '../../../../types/indicator';
import type { EnterpriseAction } from '../../../../services/actionService';
import type { LstCargo, LstExecucao, LstLayerMeeting, LstTemplate } from '../../types';
import { LST_CARGO_LABELS } from '../../types';
import { buildLayerAudit, type LayerVerdict } from '../../services/layerAuditJudgment';
import { TplEnterpriseEmptyState } from './ux/TplEnterpriseEmptyState';
import { TplAuditActionsPanel } from './audit/TplAuditActionsPanel';
import { computeTplAuditActions } from '../../services/tplAuditActions';

interface AuditLeader {
  id: string;
  name: string;
  cargo: LstCargo;
}

interface TplLayerAuditPanelProps {
  leaders: AuditLeader[];
  selectedLeaderId: string | null;
  onSelectLeader: (id: string) => void;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  weekDates: string[];
  today: string;
  auditorName: string;
  lastFieldAudit: LstLayerMeeting | null;
  maturityNote: string;
  onMaturityNoteChange: (note: string) => void;
  onApprove: () => void;
  approving?: boolean;
  indicators?: Indicator[];
  indicatorSnapshots?: IndicatorSnapshot[];
  correctiveActions?: EnterpriseAction[];
  actionsLoading?: boolean;
}

function formatAuditDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

function formatTodayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
  });
}

function toneClass(pct: number | null): string {
  if (pct == null) return 'text-slate-400';
  if (pct >= 90) return 'text-emerald-700 dark:text-emerald-400';
  if (pct >= 70) return 'text-amber-700 dark:text-amber-400';
  return 'text-red-700 dark:text-red-400';
}

function verdictClass(verdict: LayerVerdict): string {
  if (verdict === 'ok') return 'text-emerald-700 dark:text-emerald-400';
  if (verdict === 'falha') return 'text-red-700 dark:text-red-400';
  return 'text-slate-400';
}

function formatPct(pct: number | null): string {
  return pct == null ? '—' : `${pct}%`;
}

/**
 * Auditoria de camada — disciplina do líder, conformidade do operador e resultado da meta.
 */
export const TplLayerAuditPanel: React.FC<TplLayerAuditPanelProps> = ({
  leaders,
  selectedLeaderId,
  onSelectLeader,
  templates,
  execucoes,
  weekDates,
  today,
  auditorName,
  lastFieldAudit,
  maturityNote,
  onMaturityNoteChange,
  onApprove,
  approving = false,
  indicators = [],
  correctiveActions = [],
  actionsLoading = false,
}) => {
  const selected = leaders.find((l) => l.id === selectedLeaderId) ?? leaders[0] ?? null;

  const memberNames = useMemo(
    () => new Map(leaders.map((l) => [l.id, l.name])),
    [leaders],
  );

  const judgment = useMemo(
    () =>
      buildLayerAudit({
        templates,
        execucoes,
        dates: weekDates,
        today,
        indicators,
      }),
    [templates, execucoes, weekDates, today, indicators],
  );

  const auditActions = useMemo(
    () =>
      selected
        ? computeTplAuditActions({
            templates,
            execucoes,
            correctiveActions,
            userId: selected.id,
            today,
            memberNames,
          })
        : {
            desviosEncontrados: 0,
            abertas: 0,
            vencidas: 0,
            concluidas: 0,
            desviosComAcao: 0,
            recentActions: [],
          },
    [templates, execucoes, correctiveActions, selected, today, memberNames],
  );

  const showActions =
    actionsLoading ||
    auditActions.recentActions.length > 0 ||
    auditActions.abertas > 0 ||
    auditActions.vencidas > 0;

  if (leaders.length === 0) {
    return (
      <TplEnterpriseEmptyState
        icon={ClipboardCheck}
        title="Auditoria de Camada"
        description="Fluxo de gestão disponível para quem possui liderados diretos na hierarquia."
        hint="Configure reportes em Administração → Pessoas para habilitar auditorias em campo."
      />
    );
  }

  const metrics = [
    {
      label: 'Disciplina',
      hint: 'O líder',
      value: formatPct(judgment.disciplinePct),
      tone: toneClass(judgment.disciplinePct),
    },
    {
      label: 'Conformidade',
      hint: 'O operador',
      value: formatPct(judgment.conformityPct),
      tone: toneClass(judgment.conformityPct),
    },
    {
      label: 'Resultado',
      hint: 'A meta',
      value: formatPct(judgment.resultPct),
      tone: toneClass(judgment.resultPct),
    },
  ];

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
      <header className="shrink-0 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-50">
              Auditoria
            </h3>
            <p className="mt-0.5 truncate text-[12px] text-slate-500">
              {auditorName}
              {selected ? ` auditando ${selected.name}` : ''}
            </p>
          </div>
          <select
            aria-label="Liderado em auditoria"
            className="h-9 max-w-[240px] rounded-md border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-800 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"
            value={selectedLeaderId ?? selected?.id ?? ''}
            onChange={(e) => onSelectLeader(e.target.value)}
          >
            {leaders.map((leader) => (
              <option key={leader.id} value={leader.id}>
                {leader.name} · {LST_CARGO_LABELS[leader.cargo]}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-slate-500">
          <Calendar size={13} />
          Última auditoria: {formatAuditDateShort(lastFieldAudit?.meeting_date)}
          {lastFieldAudit?.adherence_pct != null && (
            <span>· {lastFieldAudit.adherence_pct}% de disciplina</span>
          )}
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {metrics.map((metric) => (
              <div
                key={metric.label}
                className="rounded-lg border border-slate-200 px-3 py-2.5 dark:border-slate-800"
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {metric.label}
                </p>
                <p className="mt-0.5 text-[12px] text-slate-400">{metric.hint}</p>
                <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-none ${metric.tone}`}>
                  {metric.value}
                </p>
              </div>
            ))}
          </div>

          <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
            <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              <h4 className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                Padrão do liderado
              </h4>
              <p className="mt-0.5 text-[12px] text-slate-500">
                Semana até {formatTodayLabel(today)}
              </p>
            </div>
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900/60">
                <tr>
                  <th className="px-4 py-2 font-semibold">Rotina</th>
                  <th className="px-2 py-2 font-semibold">Disciplina</th>
                  <th className="px-2 py-2 font-semibold">Conformidade</th>
                  <th className="px-4 py-2 font-semibold">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {judgment.rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-[13px] text-slate-500">
                      Nenhuma rotina vencida nesta semana.
                    </td>
                  </tr>
                ) : (
                  judgment.rows.map((row) => (
                    <tr key={row.templateId} className="border-t border-slate-100 dark:border-slate-800/60">
                      <td className="max-w-[220px] px-4 py-2">
                        <p className="truncate font-medium text-slate-800 dark:text-slate-100">{row.title}</p>
                        {row.timeLabel && (
                          <p className="text-[12px] tabular-nums text-slate-400">{row.timeLabel}</p>
                        )}
                      </td>
                      <td className={`px-2 py-2 font-medium ${verdictClass(row.discipline)}`}>
                        {row.disciplineLabel}
                      </td>
                      <td className={`px-2 py-2 font-medium ${verdictClass(row.conformity)}`}>
                        {row.conformityLabel}
                      </td>
                      <td className={`px-4 py-2 font-medium ${verdictClass(row.result)}`}>
                        {row.resultLabel}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>

          <section className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
            <label htmlFor="audit-maturity-note" className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
              Nota da auditoria
            </label>
            <textarea
              id="audit-maturity-note"
              value={maturityNote}
              onChange={(e) => onMaturityNoteChange(e.target.value)}
              rows={2}
              placeholder="O que você observou em campo"
              className="mt-2 w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-900/40"
            />
            <button
              type="button"
              disabled={approving || !selected}
              onClick={onApprove}
              className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-emerald-600 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 size={16} />
              {approving ? 'Registrando…' : 'Concluir auditoria'}
            </button>
          </section>

          {showActions && (
            <TplAuditActionsPanel summary={auditActions} loading={actionsLoading} />
          )}
        </div>
      </div>
    </section>
  );
};
